
import InputError from 'common/components/inputError/InputError';
import Button from 'common/components/button/Button';
import ConfirmDeleteModal from 'common/components/modal/confirmDeleteModal/ConfirmDeleteModal';
import PageBreadcrumbsPortal from 'common/components/pageBreadcrumbsPortal/PageBreadcrumbsPortal';
import PageContainer from 'common/components/pageContainer/PageContainer';
import PageHeader from 'common/components/pageHeader/PageHeader';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import { TextInputController } from 'common/components/textInput/TextInput';
import { Label } from 'common/components/texts/Texts';
import { useEffect, useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FaInfoCircle, FaTrashAlt } from 'react-icons/fa';
import { useParams, useNavigate } from 'react-router-dom';
import Loading from 'common/services/Loading';
import toast from 'react-hot-toast';
import { LOGGER_LOG_TYPE } from 'Config';
import Logger from 'common/services/Logger';
import UsersService from 'api/users/UsersService';
import { UserProfile } from 'api/account/models/UserProfile';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import Toast from 'common/services/Toast';
import { Form } from 'common/components/form/Form';
import styles from './BudgetFileModelScreen.module.scss'
import { BudgetFileModelDto, BudgetFileModelTranslationDto } from 'api/budgetFileModels/models/BudgetFileModelDto';
import BudgetFileModelsService from 'api/budgetFileModels/BudgetFileModelsService';
import { FileInputController } from 'common/components/fileInput/FileInput';
import { AttachmentDto } from 'api/common/models/AttachmentDto';
import CustomFile from 'common/models/CustomFile';
import SelectInput, { SelectInputOption } from 'common/components/selectInput/SelectInput';
import InformationModal from 'common/components/modal/informationModal/InformationModal';
import { RichInputController } from 'common/components/richTextInput/RichTextInput';
import { SmallTabs, Tab } from 'common/components/smallTabs/SmallTabs';
import LanguagesService from 'api/languages/LanguagesService';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import Utils from 'common/services/Utils';

type AttachmentMap = Record<string, AttachmentDto | null | undefined>;

function BudgetFileModelScreen(): JSX.Element | null {
    const { id, type } = useParams<{ id: string, type: 'create' | 'edit' | 'details' }>();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const url = '/backoffice/budget-file-models';
    const currentUrl = url + '/' + type + (id ? ('/' + id) : '');

    const form = useForm<BudgetFileModelDto>({ shouldUnregister: false });
    const errors = form.formState.errors;
    const isDetails = type === 'details';
    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);

    const loggedUser = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const canWrite = UsersService.hasPolicies(loggedUser?.policies ?? [], ['BUDGETS_FILE_MODELS_WRITE'])
    const canRead = UsersService.hasPolicies(loggedUser?.policies ?? [], ['BUDGETS_FILE_MODELS_READ'])

    const [languages, setLanguages] = useState<SelectValueLabel[]>([]);
    const [headerFiles, setHeaderFiles] = useState<AttachmentMap>({});
    const [footerFiles, setFooterFiles] = useState<AttachmentMap>({});
    const [conditionsFiles, setConditionsFiles] = useState<AttachmentMap>({});

    const [showInfoModal, setShowInfoModal] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<string>('');

    useEffect(() => {
        void getData();
    }, [id, type]);

    const findTranslation = (translations: BudgetFileModelTranslationDto[] | undefined, iso: string) => {
        return translations?.find(x => x.languageIso === iso);
    }

    const getData = async () => {
        try {
            Loading.show();
            const langs = await LanguagesService.GetAllForSelectItem();
            setLanguages(langs);
            const isos = langs.map(l => l.label ?? '').filter(Boolean);
            const firstIso = isos[0] ?? '';
            setActiveTab(prev => prev && isos.includes(prev) ? prev : firstIso);

            let result: Partial<BudgetFileModelDto> = {
                id,
                name: '',
                translations: isos.map(languageIso => ({ languageIso, paymentConditions: '' })),
            };

            if (id) {
                const loaded = await BudgetFileModelsService.getById(id);
                const translations = isos.map(languageIso => {
                    const existing = findTranslation(loaded.translations, languageIso);
                    return existing ?? { languageIso, paymentConditions: '' };
                });
                result = { ...loaded, translations };

                const headerMap: AttachmentMap = {};
                const footerMap: AttachmentMap = {};
                const conditionsMap: AttachmentMap = {};
                for (const iso of isos) {
                    const tr = findTranslation(loaded.translations, iso);
                    headerMap[iso] = tr?.headerAttachment;
                    footerMap[iso] = tr?.footerAttachment;
                    conditionsMap[iso] = tr?.conditionsAttachment;
                }
                setHeaderFiles(headerMap);
                setFooterFiles(footerMap);
                setConditionsFiles(conditionsMap);
            }
            form.reset(result);

            Loading.hide();
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get budget file models', error);
            toast.error(t('messages.error_load_info'));
            Loading.hide();
        }
    }

    const wireAttachment = (
        translation: BudgetFileModelTranslationDto,
        attachment: AttachmentDto | null | undefined,
        target: 'header' | 'footer' | 'conditions',
        mediasToSend: CustomFile[],
    ) => {
        const idKey = `${target}AttachmentId` as const;
        const copyKey = (target === 'header'
            ? 'copyHeaderAttachmentId'
            : target === 'footer'
                ? 'copyFooterAttachmentId'
                : 'copyConditionsAttachmentId') as keyof BudgetFileModelTranslationDto;
        const removeKey = (target === 'header'
            ? 'removeHeaderAttachment'
            : target === 'footer'
                ? 'removeFooterAttachment'
                : 'removeConditionsAttachment') as keyof BudgetFileModelTranslationDto;

        if (attachment && attachment.tempFile) {
            translation[idKey] = attachment.key;
            attachment.tempFile.containerName = attachment.key;
            mediasToSend.push(attachment.tempFile);
        } else if (attachment && attachment.copyFromAttachmentId) {
            (translation as any)[copyKey] = attachment.copyFromAttachmentId;
        } else if (attachment == null) {
            (translation as any)[removeKey] = true;
        }
    }

    const onSubmit = async (model: BudgetFileModelDto) => {
        try {
            Loading.show();
            const mediasToSend: CustomFile[] = [];
            const isos = languages.map(l => l.label ?? '').filter(Boolean);

            const translations = isos.map(iso => {
                const existing = (model.translations?.find(x => x.languageIso === iso) ?? { languageIso: iso, paymentConditions: '' });
                const translation: BudgetFileModelTranslationDto = {
                    ...existing,
                    languageIso: iso,
                    paymentConditions: existing.paymentConditions ?? '',
                };
                translation.headerAttachmentId = undefined;
                translation.footerAttachmentId = undefined;
                translation.conditionsAttachmentId = undefined;
                translation.removeHeaderAttachment = undefined;
                translation.removeFooterAttachment = undefined;
                translation.removeConditionsAttachment = undefined;
                translation.copyHeaderAttachmentId = undefined;
                translation.copyFooterAttachmentId = undefined;
                translation.copyConditionsAttachmentId = undefined;

                wireAttachment(translation, headerFiles[iso], 'header', mediasToSend);
                wireAttachment(translation, footerFiles[iso], 'footer', mediasToSend);
                wireAttachment(translation, conditionsFiles[iso], 'conditions', mediasToSend);

                return translation;
            });

            const payload: BudgetFileModelDto = {
                ...model,
                translations,
            };

            if (payload && payload.id) {
                await BudgetFileModelsService.update(payload, mediasToSend);
                void getData();
            } else if (payload) {
                const newId = await BudgetFileModelsService.create(payload, mediasToSend);
                navigateTo('edit', newId);
            }
            setHeaderFiles({});
            setFooterFiles({});
            setConditionsFiles({});
            Loading.hide();
            toast.success(t('messages.record_save_success'));
        } catch (error: any) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't update the budget file model with id: ${id}`, error);
            toast.error(t('messages.record_save_error'));
            Loading.hide();
        }
    };

    const onInvalid = () => {
        Toast.warning(t('messages.required_fields_empty'));
    }

    const onDelete = async (item: boolean) => {
        if (!item) {
            setShowConfirmDeleteModal(false)
            return;
        }
        try {
            Loading.show();

            await BudgetFileModelsService.remove(form.getValues())
            navigateTo();

            Loading.hide();
            toast.success(t('messages.record_delete_success'));
        } catch (error: any) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t delete budget file model', error);
            toast.error(t('messages.record_delete_error'));
        }
        setShowConfirmDeleteModal(false)
    }

    const navigateTo = (typeUrl?: string, id?: string) => {
        if (typeUrl) {
            navigate(`${url}/${typeUrl}/${id}`);
        } else {
            navigate(url);
        }
    }

    const entityName = form.watch('name');
    const titlePrefix = type === 'create'
        ? t('common.new_title.budget_file_model')
        : t('common.details_title.budget_file_model');
    const entityLabel = type !== 'create' && entityName ? entityName : undefined;
    const title = entityLabel ? `${titlePrefix} ${entityLabel}` : titlePrefix;

    if (!canRead) {
        return null;
    }

    const languageName = (iso: string): string => {
        const fallback = languages.find(l => l.label === iso)?.fullLabel ?? iso;
        const key = `languages.${iso}`;
        const translated = t(key);
        return translated === key ? fallback : translated;
    }

    const copyOptions = (filesMap: AttachmentMap, currentIso: string): SelectInputOption[] => {
        return languages
            .map(l => l.label ?? '')
            .filter(otherIso => otherIso && otherIso !== currentIso)
            .map(otherIso => {
                const attachment = filesMap[otherIso];
                const hasSource = !!attachment && (!!attachment.attachmentId || !!attachment.tempFile || !!attachment.copyFromAttachmentId);
                if (!hasSource) return null;
                return { value: otherIso, label: t('budget_file_models.copy_from', { language: languageName(otherIso) }) };
            })
            .filter((x): x is SelectInputOption => x !== null);
    }

    const onCopyAttachment = (
        filesMap: AttachmentMap,
        setFilesMap: React.Dispatch<React.SetStateAction<AttachmentMap>>,
        targetIso: string,
        target: 'header' | 'footer' | 'conditions',
        sourceIso: string | null | undefined,
    ) => {
        if (!sourceIso) return;
        const sourceAttachment = filesMap[sourceIso];
        if (!sourceAttachment) return;

        let copy: AttachmentDto;
        if (sourceAttachment.attachmentId) {
            // Source already persisted on the server → backend will duplicate via CopyXxxAttachmentId
            copy = {
                id: null,
                key: sourceAttachment.key,
                attachmentId: undefined,
                url: sourceAttachment.url,
                name: sourceAttachment.name,
                contentType: sourceAttachment.contentType,
                size: sourceAttachment.size,
                tempFile: null,
                copyFromAttachmentId: sourceAttachment.attachmentId,
            };
        } else if (sourceAttachment.tempFile) {
            // Source is a pending upload → clone the underlying File so both translations upload independently
            const newKey = Utils.newGuid();
            const cloned = new File(
                [sourceAttachment.tempFile],
                sourceAttachment.tempFile.name,
                { type: sourceAttachment.tempFile.type },
            ) as CustomFile;
            cloned.containerName = newKey;
            copy = {
                id: null,
                key: newKey,
                attachmentId: undefined,
                url: sourceAttachment.url,
                name: sourceAttachment.name,
                contentType: sourceAttachment.contentType,
                size: sourceAttachment.size,
                tempFile: cloned,
            };
        } else {
            return;
        }
        setFilesMap(s => ({ ...s, [targetIso]: copy }));

        const langIndex = languages.findIndex(l => l.label === targetIso);
        if (langIndex >= 0) {
            const fieldName = `translations.${langIndex}.${target}Attachment` as const;
            form.setValue(fieldName as any, copy as any);
        }
    }

    const renderTranslationFields = (langIndex: number, iso: string) => (
        <>
            <Row className="mb-3">
                <Col>
                    <Label space>
                        {t('budget_file_models.header_file')}&nbsp;
                        <FaInfoCircle className={styles.infoCircle} onClick={() => setShowInfoModal(true)} />
                    </Label>
                    <Row>
                        <Col xs={12} md={headerFiles[iso] ? 12 : 8}>
                            <FileInputController
                                name={`translations.${langIndex}.headerAttachment` as const}
                                value={headerFiles[iso]}
                                control={form.control}
                                accept={{
                                    'image/png': ['.png'],
                                    'image/jpeg': ['.jpg', '.jpeg'],
                                }}
                                onChange={(file) => setHeaderFiles(s => ({ ...s, [iso]: file }))}
                                disabled={isDetails}
                            />
                        </Col>
                        {!headerFiles[iso] && (
                            <Col xs={12} md={4} className="d-flex align-items-center">
                                <span className="me-2">{t('common.or')}</span>
                                <div style={{ flex: 1 }}>
                                    <SelectInput
                                        value={null}
                                        options={copyOptions(headerFiles, iso)}
                                        onChange={(v) => onCopyAttachment(headerFiles, setHeaderFiles, iso, 'header', v)}
                                        disabled={isDetails}
                                    />
                                </div>
                            </Col>
                        )}
                    </Row>
                </Col>
            </Row>
            <Row className="mb-3">
                <Col>
                    <Label space>
                        {t('budget_file_models.footer_file')}&nbsp;
                        <FaInfoCircle className={styles.infoCircle} onClick={() => setShowInfoModal(true)} />
                    </Label>
                    <Row>
                        <Col xs={12} md={footerFiles[iso] ? 12 : 8}>
                            <FileInputController
                                name={`translations.${langIndex}.footerAttachment` as const}
                                value={footerFiles[iso]}
                                control={form.control}
                                accept={{
                                    'image/png': ['.png'],
                                    'image/jpeg': ['.jpg', '.jpeg'],
                                }}
                                onChange={(file) => setFooterFiles(s => ({ ...s, [iso]: file }))}
                                disabled={isDetails}
                            />
                        </Col>
                        {!footerFiles[iso] && (
                            <Col xs={12} md={4} className="d-flex align-items-center">
                                <span className="me-2">{t('common.or')}</span>
                                <div style={{ flex: 1 }}>
                                    <SelectInput
                                        value={null}
                                        options={copyOptions(footerFiles, iso)}
                                        onChange={(v) => onCopyAttachment(footerFiles, setFooterFiles, iso, 'footer', v)}
                                        disabled={isDetails}
                                    />
                                </div>
                            </Col>
                        )}
                    </Row>
                </Col>
            </Row>
            <Row className="mb-3">
                <Col>
                    <Label space>{t('budget_file_models.terms_and_conditions_file')}</Label>
                    <Row>
                        <Col xs={12} md={conditionsFiles[iso] ? 12 : 8}>
                            <FileInputController
                                name={`translations.${langIndex}.conditionsAttachment` as const}
                                value={conditionsFiles[iso]}
                                control={form.control}
                                accept={{
                                    'application/pdf': ['.pdf'],
                                }}
                                onChange={(file) => setConditionsFiles(s => ({ ...s, [iso]: file }))}
                                disabled={isDetails}
                            />
                        </Col>
                        {!conditionsFiles[iso] && (
                            <Col xs={12} md={4} className="d-flex align-items-center">
                                <span className="me-2">{t('common.or')}</span>
                                <div style={{ flex: 1 }}>
                                    <SelectInput
                                        value={null}
                                        options={copyOptions(conditionsFiles, iso)}
                                        onChange={(v) => onCopyAttachment(conditionsFiles, setConditionsFiles, iso, 'conditions', v)}
                                        disabled={isDetails}
                                    />
                                </div>
                            </Col>
                        )}
                    </Row>
                </Col>
            </Row>

            <Row>
                <Col>
                    <Label space>{t('budget_file_models.payment_conditions')}</Label>
                    <RichInputController
                        name={`translations.${langIndex}.paymentConditions` as const}
                        control={form.control}
                        placeholder={t('elements.description')}
                        disabled={isDetails}
                        rules={{}}
                        hasError={false}
                    />
                </Col>
            </Row>
        </>
    );

    return (<ScreenTitle title={t('budget_file_models.title')}>
        <PageBreadcrumbsPortal
            breadcrumbs={[
                { name: t('home.title'), url: '/' },
                { name: t('budget_file_models.title'), url },
                { name: title, url: currentUrl },
            ]}
        />

        <PageHeader title={titlePrefix} subTitle={entityLabel} informationText={t('common.go_back')} onGoBack={() => navigateTo()}>
            {canWrite && !isDetails && <Button type='submit' form='modelForm'>{t('common.save')}</Button>}
            {canWrite && isDetails && <Button fw href={`${url}/edit/${id}`}>{t('common.edit')}</Button>}
            {canWrite && isDetails && <Button variant='secondary' onClick={() => setShowConfirmDeleteModal(true)}> <FaTrashAlt /> </Button>}
        </PageHeader>
        <PageContainer addBottomSpace>
            <Form handleSubmit={form.handleSubmit} onSubmit={onSubmit} onInvalid={onInvalid} noValidate id="modelForm">
                <Row className="mb-3">
                    <Col sm={12} md={12} >
                        <Label space>{t('budget_file_models.name')}{!isDetails ? '*' : ''}</Label>
                        <TextInputController
                            name='name' control={form.control}
                            placeholder={t('budget_file_models.name')} disabled={isDetails}
                            rules={{ required: true, maxLength: 250 }}
                            hasError={Boolean(errors.name)} />
                        <InputError error={errors.name} maxLength={250} />
                    </Col>
                </Row>
                {languages.length > 0 && activeTab && (
                    <SmallTabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
                        {languages.map((lang, idx) => (
                            <Tab key={lang.label ?? idx} eventKey={lang.label ?? ''} title={languageName(lang.label ?? '')}>
                                {renderTranslationFields(idx, lang.label ?? '')}
                            </Tab>
                        ))}
                    </SmallTabs>
                )}
            </Form>
        </PageContainer>

        <ConfirmDeleteModal
            itemName={form.getValues('name')}
            isOpen={showConfirmDeleteModal}
            onClose={(result) => onDelete(result)}
        />
        <InformationModal
            message={t('budget_file_models.banner_sizes')}
            isOpen={showInfoModal}
            onClose={() => setShowInfoModal(false)}
        />
    </ScreenTitle>)
}

export default BudgetFileModelScreen;
